import React, { useEffect, useState } from 'react';
import { Type, Wand2, Sparkles, Save } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { adminAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';

const TypographySettings = () => {
  const { token } = useAuth();
  const { updateTypography, refreshTypography, typography } = useTheme();
  const { toast } = useToast();
  const [presets, setPresets] = useState([]);
  const [formData, setFormData] = useState({
    name: 'Default Sans',
    headlineFont: "'Inter', system-ui, -apple-system, sans-serif",
    bodyFont: "'Inter', system-ui, -apple-system, sans-serif",
    accentFont: "'Inter', system-ui, -apple-system, sans-serif",
    baseFontSize: '16px',
    headingWeight: '700',
    bodyWeight: '400',
    headingLetterSpacing: '-0.02em',
    bodyLetterSpacing: '-0.01em',
    kickerTransform: 'uppercase'
  });
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [settingsRes, presetRes] = await Promise.all([
        adminAPI.getTypographySettings(token),
        adminAPI.getTypographyPresets(token)
      ]);

      if (presetRes.success) {
        setPresets(presetRes.data.presets || []);
      }

      if (settingsRes.success && settingsRes.data) {
        setFormData(settingsRes.data);
        updateTypography(settingsRes.data);
        const matchedPreset = (presetRes.data?.presets || []).find(
          (preset) => preset.name === settingsRes.data.name
        );
        if (matchedPreset) {
          setSelectedPreset(matchedPreset.id);
        }
      }
    } catch (error) {
      toast({
        title: 'Unable to load typography settings',
        description: 'Using current defaults.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPreset = (preset) => {
    setSelectedPreset(preset.id);
    setFormData(preset);
    updateTypography(preset);
  };

  const handleChange = (field, value) => {
    const updated = { ...formData, [field]: value };
    setFormData(updated);
    updateTypography(updated);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await adminAPI.updateTypography(formData, token);
      if (response.success) {
        toast({
          title: 'Typography saved',
          description: 'Updated across the portfolio.',
        });
        await refreshTypography();
      }
    } catch (error) {
      toast({
        title: 'Save failed',
        description: 'Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Sparkles className="animate-pulse text-orange-400" />
      </div>
    );
  }

  return (
    <div className="glass rounded-2xl border border-orange-500/20 p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-orange-400 mb-1">Typography</p>
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            <Type size={18} className="text-orange-400" />
            Editorial/Modern Type System
          </h3>
          <p className="text-sm text-gray-400">
            Swap between presets inspired by the Modern Portfolio brief or tweak font stacks per layer.
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-gradient-to-r from-orange-500 to-orange-600 text-white border-0"
        >
          {saving ? (
            <>
              <Wand2 size={16} className="mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save size={16} className="mr-2" />
              Save Typography
            </>
          )}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-gray-300">Theme name</Label>
              <Input
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="bg-zinc-900 border-gray-800 text-white"
              />
            </div>
            <div>
              <Label className="text-gray-300">Base font size</Label>
              <Input
                value={formData.baseFontSize}
                onChange={(e) => handleChange('baseFontSize', e.target.value)}
                placeholder="16px"
                className="bg-zinc-900 border-gray-800 text-white"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-gray-300">Headline font</Label>
              <Input
                value={formData.headlineFont}
                onChange={(e) => handleChange('headlineFont', e.target.value)}
                className="bg-zinc-900 border-gray-800 text-white"
              />
            </div>
            <div>
              <Label className="text-gray-300">Body font</Label>
              <Input
                value={formData.bodyFont}
                onChange={(e) => handleChange('bodyFont', e.target.value)}
                className="bg-zinc-900 border-gray-800 text-white"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-gray-300">Accent font</Label>
              <Input
                value={formData.accentFont}
                onChange={(e) => handleChange('accentFont', e.target.value)}
                className="bg-zinc-900 border-gray-800 text-white"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-300">Heading weight</Label>
                <Input
                  value={formData.headingWeight}
                  onChange={(e) => handleChange('headingWeight', e.target.value)}
                  className="bg-zinc-900 border-gray-800 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-300">Body weight</Label>
                <Input
                  value={formData.bodyWeight}
                  onChange={(e) => handleChange('bodyWeight', e.target.value)}
                  className="bg-zinc-900 border-gray-800 text-white"
                />
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-300">Heading letter spacing</Label>
                <Input
                  value={formData.headingLetterSpacing}
                  onChange={(e) => handleChange('headingLetterSpacing', e.target.value)}
                  placeholder="-0.04em"
                  className="bg-zinc-900 border-gray-800 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-300">Body letter spacing</Label>
                <Input
                  value={formData.bodyLetterSpacing}
                  onChange={(e) => handleChange('bodyLetterSpacing', e.target.value)}
                  placeholder="-0.01em"
                  className="bg-zinc-900 border-gray-800 text-white"
                />
              </div>
            </div>
            <div>
              <Label className="text-gray-300">Kicker transform</Label>
              <select
                value={formData.kickerTransform}
                onChange={(e) => handleChange('kickerTransform', e.target.value)}
                className="w-full rounded-md bg-zinc-900 border border-gray-800 px-3 py-2 text-sm text-white"
              >
                <option value="uppercase">Uppercase (default)</option>
                <option value="none">None</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Affects section kickers/eyebrows.</p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="rounded-xl border border-orange-500/30 bg-orange-500/5 p-3">
            <div className="flex items-center gap-2 text-orange-300 text-sm font-medium">
              <Wand2 size={16} />
              Quick presets (Modern Portfolio ready)
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-1 gap-2">
            {presets.map((preset) => (
              <button
                key={preset.id}
                onClick={() => handleSelectPreset(preset)}
                className={`p-3 rounded-xl text-left border transition-all duration-150 ${
                  selectedPreset === preset.id
                    ? 'border-orange-500 bg-orange-500/10 text-white'
                    : 'border-gray-800 bg-zinc-900 text-gray-300 hover:border-orange-500/40'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold">{preset.name}</p>
                    <p className="text-xs text-gray-400 truncate">
                      {preset.headlineFont.split(',')[0]} / {preset.bodyFont.split(',')[0]}
                    </p>
                  </div>
                  {preset.name.toLowerCase().includes('modern') && (
                    <span className="text-[10px] px-2 py-1 rounded-full bg-orange-500/20 text-orange-300">
                      Recommended
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
          <div className="p-3 rounded-xl border border-gray-800 bg-zinc-900 text-xs text-gray-400 space-y-1">
            <p className="font-semibold text-gray-200">Live preview stack</p>
            <p>Heading: {typography?.headlineFont || formData.headlineFont}</p>
            <p>Body: {typography?.bodyFont || formData.bodyFont}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TypographySettings;
